import { ExtranetShopOrderStatusEnum, MultilingualKeyDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledCheckboxes,
  ControlledDatePicker,
  ControlledInput,
  ExtranetContainer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton, PharmacIcon } from '@pharmac/ui'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../../../components/filters/ExtranetFiltersComponents'
import { cleanQueryParams } from '../../../features/infinite-query/util/cleanQueryParams'
import { GetExtranetShopOrderParams } from './ShopOrderHistory'
import { mapShopOrderParamsToQueryParams } from './util/query-parameter-mapping'

type ShopOrderHistoryFiltersProps = {
  closeFilters: () => void
}

export const ShopOrderHistoryFilters = ({ closeFilters }: ShopOrderHistoryFiltersProps) => {
  const { t } = useLanguage(['extranet', 'common'])
  const { minDesktop } = useBreakpoint()

  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapShopOrderParamsToQueryParams(searchParams))

  const formHook = useForm<GetExtranetShopOrderParams>({
    mode: 'onChange',
    defaultValues: {
      orderCreatedAt: filters.orderCreatedAt,
      buyer: filters.buyer ?? '',
      orderNumber: filters.orderNumber ?? undefined,
      status: filters.status ?? [],
    },
  })

  useEffect(() => {
    const params = mapShopOrderParamsToQueryParams(searchParams)

    setFilters(params)

    formHook.reset({
      orderCreatedAt: params.orderCreatedAt ?? '',
      buyer: params.buyer ?? '',
      orderNumber: params.orderNumber ?? undefined,
      status: params.status ?? [],
    })
  }, [searchParams])

  const handleSubmit = (values: GetExtranetShopOrderParams) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )
    closeFilters()
  }

  const orderStatus: Array<{ value: ExtranetShopOrderStatusEnum; name: MultilingualKeyDto }> = [
    {
      value: ExtranetShopOrderStatusEnum.Arrived,
      name: {
        fi: t('shop.orders.statuses.arrived'),
        en: t('shop.orders.statuses.arrived'),
        sv: t('shop.orders.statuses.arrived'),
      },
    },
    {
      value: ExtranetShopOrderStatusEnum.BeingProcessed,
      name: {
        fi: t('shop.orders.statuses.beingProcessed'),
        en: t('shop.orders.statuses.beingProcessed'),
        sv: t('shop.orders.statuses.beingProcessed'),
      },
    },
    {
      value: ExtranetShopOrderStatusEnum.Completed,
      name: {
        fi: t('shop.orders.statuses.completed'),
        en: t('shop.orders.statuses.completed'),
        sv: t('shop.orders.statuses.completed'),
      },
    },
    {
      value: ExtranetShopOrderStatusEnum.Cancelled,
      name: {
        fi: t('shop.orders.statuses.cancelled'),
        en: t('shop.orders.statuses.cancelled'),
        sv: t('shop.orders.statuses.cancelled'),
      },
    },
  ]

  return (
    <>
      <FormProvider {...formHook}>
        <ExtranetFiltersRow style={{ height: minDesktop ? '250px' : undefined }}>
          <ExtranetFiltersColumn>
            <ControlledCheckboxes
              column
              data={orderStatus}
              name="status"
              control={formHook.control}
              label={t('shop.orders.orderStatus')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledInput
              label={t('shop.orders.orderNumber')}
              name="orderNumber"
              control={formHook.control}
              posticon={
                <ExtranetContainer marginMediumHorizontal>
                  <PharmacIcon icon="search" />
                </ExtranetContainer>
              }
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledDatePicker
              control={formHook.control}
              label={t('shop.orders.orderDate')}
              name="orderCreatedAt"
              placeholder={t('common:selectDate')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledInput
              label={t('shop.orders.purchaserName')}
              name="buyer"
              control={formHook.control}
              posticon={
                <ExtranetContainer marginMediumHorizontal>
                  <PharmacIcon icon="search" />
                </ExtranetContainer>
              }
            />
          </ExtranetFiltersColumn>
        </ExtranetFiltersRow>
        <ExtranetFiltersFooter>
          <PharmacButton
            onClick={formHook.handleSubmit(handleSubmit, (a) => console.log(a))}
            disabled={!formHook.formState.isDirty}
          >
            {t('search', { ns: 'common' })}
          </PharmacButton>
        </ExtranetFiltersFooter>
      </FormProvider>
    </>
  )
}
